import React, { useState } from "react";
import { IoMdEye } from "react-icons/io";
import { RiDeleteBin6Line } from "react-icons/ri";
import { Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  deleteRestaurant,
  getAllRestaurants,
  updateTableBookings,
} from "../../../api/owner/restaurants";
import Pagination from "../../../components/common/Pagination";
import toast from "react-hot-toast";
import Swal from "sweetalert2";
import { useTranslation } from "../../../hooks/useTranslation";

const Restaurant = () => {
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState(true);
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const limit = 10;
  const navigate = useNavigate();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ["restaurants", page],
    queryFn: () => getAllRestaurants({ page, limit }),
    keepPreviousData: true,
  });

  const { mutate: deleteMutation } = useMutation({
    mutationFn: deleteRestaurant,
    onSuccess: () => {
      queryClient.invalidateQueries(["restaurants"]);
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        t("forRestaurants").messages.deleteError;
      toast.error(errorMessage);
    },
  });

  const tableBookingMutation = useMutation({
    mutationFn: (data) => updateTableBookings(data),
    onSuccess: () => {
      toast.success(t("forRestaurants").messages.updateSuccess);
      queryClient.invalidateQueries(["restaurants"]);
    },
    onError: () => {
      toast.error(t("forRestaurants").messages.updateError);
    },
  });

  const restaurants = data?.data?.restaurants || [];
  const pagination = data?.data?.pagination || {};

  const handleDelete = (id) => {
    Swal.fire({
      title: t("forRestaurants").confirmDialog.deleteTitle,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("forRestaurants").confirmDialog.deleteConfirmBtn,
      cancelButtonText: t("forRestaurants").confirmDialog.deleteDenyBtn,
    }).then((result) => {
      if (result.isConfirmed) {
        deleteMutation(id);
      }
    });
  };

  const handleToggleTableBookings = (restaurantId, currentValue, status) => {
    if (status === "rejected" || status === "pending") {
      return false;
    }

    tableBookingMutation.mutate({
      restaurantId,
      tableBookingEnabled: !currentValue,
    });
  };

  return (
    <>
      <div className="flex md:flex-row flex-wrap justify-between items-center gap-[10px]">
        <h2 className="md:text-[28px] text-lg font-semibold text-black">
          {t("addrestaurant").addNew}
        </h2>
        <Link
          to="/addnewrestaurant"
          className="inline-block w-full sm:max-w-[205px] rounded-[2px] bg-[#4CAF50] 2xl:text-[20px] text-base text-white text-center font-semibold 2xl:p-[9px] p-3 green-shadow hover:bg-[#209b25]"
        >
          {t("addrestaurant").addNewButton}
        </Link>
      </div>

      <div className="mt-[31px]">
        <div className="rounded-[10px] border border-[#D9D9D9] overflow-hidden mt-[28px] py-[20px] sm:px-[30px] px-[15px] bg-white grid relative">
          <h2 className="text-base font-semibold">
            {t("addrestaurant").restaurantList}
          </h2>

          <div className="overflow-x-auto grid">
            <table className="w-full ">
              <thead className="bg-[#FFFAF2] ">
                <tr className="">
                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("addrestaurant").name}
                  </th>
                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("addrestaurant").openingTime}
                  </th>
                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("addrestaurant").closingTime}
                  </th>

                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("addrestaurant").contact}
                  </th>

                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("addrestaurant").verificationStatus}
                  </th>

                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("addrestaurant").rejectionReason}
                  </th>

                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("addrestaurant").menuOnlyMode}
                  </th>

                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("addrestaurant").tableBookings}
                  </th>

                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("addrestaurant").floorPlan}
                  </th>

                  <th className="text-left text-sm font-medium text-[#000000] py-[12px] px-[16px] whitespace-nowrap">
                    {t("addrestaurant").actions}
                  </th>
                </tr>
              </thead>
              <tbody className="w-full "> 
                {isLoading ? (
                  <div className=" w-full max-w-full h-screen">
                    <div className="absolute inset-0 top-[30%] w-12 mx-auto h-12 border-4 border-green-500 border-t-transparent rounded-full animate-spin"></div>
                  </div>
                ) : isError ? (
                  <p className="text-center mt-6 text-red-500">
                    {error.message}
                  </p>
                ) : (
                  restaurants?.map((restaurant) => (
                    <tr
                      key={restaurant._id}
                      className="border-t border-[#E4E4E4]"
                    >
                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black">
                        {restaurant.name}
                      </td>
                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black whitespace-nowrap">
                        {restaurant.openingTime}
                      </td>
                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black whitespace-nowrap">
                        {restaurant.closingTime}
                      </td>
                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black whitespace-nowrap">
                        {restaurant.contact}
                      </td>
                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black  whitespace-nowrap">
                        <div
                          className={`p-[2px_8px_2px_8px] rounded-full w-fit  text-center text-sm  text-white ${
                            restaurant?.verificationStatus === "approved"
                              ? "bg-[#4CAF50]"
                              : restaurant?.verificationStatus === "rejected"
                              ? "bg-[#FF0000]"
                              : "bg-[#FF9D00]"
                          }`}
                        >
                          {restaurant?.verificationStatus
                            ?.charAt(0)
                            .toUpperCase() +
                            restaurant?.verificationStatus?.slice(1)}
                        </div>
                      </td>

                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black whitespace-normal">
                        <div className=" text-[14px] font-normal text-black whitespace-normal break-words">
                          {restaurant?.verificationStatus === "rejected"
                            ? restaurant?.rejectionReason
                            : "-"}
                        </div>
                      </td>

                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black whitespace-nowrap">
                        {restaurant.menuOnlyMode ? "Active" : "Inactive"}
                      </td>

                      <td className="py-[12px] px-[16px]">
                        {restaurant?.verificationStatus ===
                        "rejected" ? null : (
                          <div
                            onClick={() =>
                              handleToggleTableBookings(
                                restaurant._id,
                                restaurant.tableBookingEnabled,
                                restaurant.verificationStatus
                              )
                            }
                            className={`w-[48px] h-[20px] mx-auto flex items-center rounded-full p-1 cursor-pointer transition-colors ${
                              restaurant.tableBookingEnabled
                                ? "bg-[#4CAF50]"
                                : "bg-[#e1e1e1]"
                            }`}
                          >
                            <div
                              className={`bg-white w-[16px] h-[16px] rounded-full shadow-md transform transition-transform ${
                                restaurant.tableBookingEnabled
                                  ? "translate-x-6"
                                  : "translate-x-0"
                              }`}
                            />
                          </div>
                        )}
                      </td>

                      <td className="py-[12px] px-[16px] text-[14px] font-normal text-black-700">
                        {restaurant.tableBookingEnabled &&
                        restaurant.verificationStatus === "approved" ? (
                          <span
                            onClick={() =>
                              navigate(`/floor-plan`, {
                                state: {
                                  restaurantId: restaurant?._id,
                                },
                              })
                            }
                            className="text-green-700 cursor-pointer hover:underline"
                          >
                            {t("addrestaurant").view}
                          </span>
                        ) : (
                          "-"
                        )}
                      </td>

                      <td className="py-[12px] px-[16px] ">
                        <div className="flex gap-[12px]">
                          {restaurant?.verificationStatus ===
                        "rejected" ? null : (
                          <Link
                            to={{
                              pathname: "/addnewrestaurant",
                            }}
                            state={{
                              isEditing: true,
                              restaurantData: restaurant,
                            }}
                            className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2]"
                          >
                            <IoMdEye className="text-base text-black" />
                          </Link>
                        )}

                        <button
                          onClick={() => handleDelete(restaurant?._id)}
                          className="text-black font-normal w-[32px] h-[32px] flex items-center justify-center rounded-full bg-[#F2F2F2]"
                        >
                          <RiDeleteBin6Line className="text-base text-black" />
                        </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
            <Pagination
              page={pagination.page}
              totalPages={pagination.totalPages}
              setPage={setPage}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default Restaurant;
